import { useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  ImageOff,
  LoaderCircle,
} from "lucide-react";
import { Button } from "../../design-system";
import "./execution-detail.css";
export type CaptureAsset = {
  id: string;
  label: string;
  src: string;
  state?: "ready" | "loading" | "error" | "expired";
};
export type EvidenceState = NonNullable<CaptureAsset["state"]>;
export const sampleCaptures = (
  labels: string[],
  state: EvidenceState = "ready",
): CaptureAsset[] =>
  labels.map((label, i) => ({
    id: String(i),
    label,
    src: `/meta-crew-captures/capture-${(i % 4) + 1}.svg`,
    state,
  }));
export function CaptureImage({
  asset,
  thumbnail = false,
}: {
  asset: CaptureAsset;
  thumbnail?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const state = failed
    ? "error"
    : retry > 0
      ? "ready"
      : (asset.state ?? "ready");
  if (state !== "ready")
    return (
      <div className="lwe-image-state" role="status">
        {state === "loading" ? (
          <LoaderCircle size={24} />
        ) : (
          <ImageOff size={24} />
        )}
        {!thumbnail && (
          <>
            <strong>
              {state === "loading"
                ? "캡처를 불러오는 중"
                : state === "expired"
                  ? "보관 기간이 지난 캡처입니다"
                  : "캡처를 불러오지 못했습니다"}
            </strong>
            <small>
              {state === "expired"
                ? "결과 요약과 검토 이력은 계속 확인할 수 있습니다."
                : state === "loading"
                  ? "잠시만 기다려 주세요."
                  : "연결 상태를 확인한 뒤 다시 시도해 주세요."}
            </small>
            {state === "error" && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setFailed(false);
                  setRetry(retry + 1);
                }}
              >
                다시 불러오기
              </Button>
            )}
          </>
        )}
      </div>
    );
  return (
    // eslint-disable-next-line @next/next/no-img-element -- Framework-neutral evidence UI accepts original capture URLs.
    <img
      key={retry}
      className={thumbnail ? "lwe-thumb-img" : "lwp-capture-img"}
      src={asset.src}
      alt={asset.label}
      onError={() => setFailed(true)}
    />
  );
}
export function EvidenceViewer({ captures }: { captures: CaptureAsset[] }) {
  const [index, setIndex] = useState(0);
  const [mode, setMode] = useState("fit");
  const current = captures[Math.min(index, captures.length - 1)];
  if (!current)
    return (
      <div className="lwe-image-state">
        <ImageOff />
        <strong>확보된 캡처가 없습니다</strong>
        <small>게시 완료 후 증거를 확인할 수 있습니다.</small>
      </div>
    );
  const move = (n: number) =>
    setIndex(Math.max(0, Math.min(captures.length - 1, index + n)));
  return (
    <section
      className="lwe-viewer"
      aria-label="증거 캡처 보기"
      tabIndex={0}
      onKeyDown={(e) => {
        if ((e.target as HTMLElement).matches("input,textarea,select")) return;
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
          e.preventDefault();
          move(e.key === "ArrowLeft" ? -1 : 1);
        }
      }}
    >
      <div className="lwe-toolbar">
        <div className="lwe-controls">
          <Button
            variant="ghost"
            size="sm"
            aria-label="이전 캡처"
            disabled={index === 0}
            onClick={() => move(-1)}
          >
            <ChevronLeft size={16} />
          </Button>
          <strong aria-live="polite">
            {index + 1} / {captures.length}
          </strong>
          <Button
            variant="ghost"
            size="sm"
            aria-label="다음 캡처"
            disabled={index === captures.length - 1}
            onClick={() => move(1)}
          >
            <ChevronRight size={16} />
          </Button>
        </div>
        <div className="lwe-controls">
          <Button
            size="sm"
            variant="secondary"
            aria-pressed={mode === "fit"}
            onClick={() => setMode("fit")}
          >
            전체 맞춤
          </Button>
          <Button
            size="sm"
            variant="secondary"
            aria-pressed={mode === "scroll"}
            onClick={() => setMode("scroll")}
          >
            너비 맞춤
          </Button>
        </div>
        {(current.state ?? "ready") === "ready" && (
          <a
            href={current.src}
            target="_blank"
            rel="noreferrer"
            className="lwe-original"
          >
            원본 새 창 <ExternalLink size={13} />
          </a>
        )}
      </div>
      <div
        className={`lwe-stage is-${mode}`}
        tabIndex={mode === "scroll" ? 0 : -1}
        aria-label={
          mode === "scroll" ? "캡처 세로 스크롤 영역" : "캡처 전체 맞춤"
        }
      >
        <CaptureImage key={current.id + current.state} asset={current} />
      </div>
      <p className="lwe-caption">
        {current.label} ·{" "}
        {mode === "fit"
          ? "전체 이미지 표시"
          : "위쪽부터 표시 · 영역 안에서 스크롤"}
      </p>
      <div className="lwe-filmstrip" aria-label="캡처 선택">
        {captures.map((asset, i) => (
          <button
            key={asset.id}
            className={i === index ? "is-selected" : ""}
            aria-label={`캡처 ${i + 1}: ${asset.label}`}
            aria-pressed={i === index}
            onClick={() => setIndex(i)}
          >
            <CaptureImage asset={asset} thumbnail />
            <span>{i + 1}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
